import { heroStory, sideLive, liveTimeline } from "@/mocks/news";

const kindColor: Record<string, string> = {
  现场: "bg-primary-500",
  进展: "bg-accent-500",
  处置: "bg-secondary-500",
  评论: "bg-foreground-700",
};

export default function Hero() {
  return (
    <section id="live" className="relative pt-24 md:pt-28">
      <div className="mx-auto max-w-7xl px-4 md:px-6 pb-10">
        <div className="flex flex-col gap-6">
          {/* lead + right column */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* lead story */}
            <article className="lg:col-span-2 group cursor-pointer">
              <div className="relative overflow-hidden rounded-lg">
                <div className="aspect-[16/9] w-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=firefighters%20extinguishing%20fire%20under%20a%20city%20highway%20bridge%20with%20multiple%20fire%20trucks%20and%20hoses%2C%20daylight%20scene%2C%20crowd%20of%20bystanders%2C%20documentary%20breaking%20news%20photography%2C%20dramatic%20but%20controlled&width=1600&height=900&seq=hero-fire-x1&orientation=landscape"
                    alt="滨海跨线桥火情处置现场，消防员正在进行控火作业"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 text-background-50 text-xs font-semibold px-2.5 py-1">
                    <i className="ri-radio-2-fill" /> 现场直击
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-md bg-black/55 text-background-50 text-xs px-2.5 py-1 backdrop-blur">
                    <i className="ri-eye-line" /> {heroStory.views} 人在看
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <p className="text-primary-300 text-xs md:text-sm font-semibold mb-2 flex items-center gap-2">
                    <i className="ri-map-pin-2-line" /> {heroStory.location}
                  </p>
                  <h1 className="font-heading text-xl md:text-3xl lg:text-[2rem] font-bold leading-snug text-background-50">
                    {heroStory.title}
                  </h1>
                  <p className="hidden sm:block mt-3 text-sm text-background-200 leading-relaxed max-w-3xl">
                    {heroStory.excerpt}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-background-300">
                    <span className="inline-flex items-center gap-1"><i className="ri-user-voice-line" /> {heroStory.source}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-time-line" /> {heroStory.time}更新</span>
                  </div>
                </div>
              </div>
            </article>

            {/* side live */}
            <aside className="flex flex-col rounded-lg border border-background-200 bg-background-100 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-background-200 bg-background-50">
                <h2 className="flex items-center gap-2 font-heading font-bold text-foreground-950">
                  <span className="w-2 h-2 rounded-full bg-primary-500 live-dot" />
                  正在发生
                </h2>
                <span className="text-[11px] text-foreground-400">实时滚动中</span>
              </div>
              <ul className="flex-1 divide-y divide-background-200">
                {sideLive.map((item, i) => (
                  <li key={item.id}>
                    <a href="#live" className="flex gap-3 p-3.5 hover:bg-background-50 transition-colors group">
                      <span className="shrink-0 w-9 flex flex-col items-center">
                        <span className="text-[10px] text-foreground-400 leading-none">14:{String(58 - i * 4).padStart(2, "0")}</span>
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] leading-snug text-foreground-800 group-hover:text-primary-700 font-medium line-clamp-2">
                          {item.title}
                        </span>
                        <span className="mt-1 flex items-center gap-2 text-[11px] text-foreground-400">
                          <span className="text-primary-500 font-medium">{item.cat}</span>
                          <span>{item.time}</span>
                        </span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </aside>
          </div>

          {/* minute-by-minute timeline */}
          <div className="rounded-lg border border-background-200 bg-background-100 overflow-hidden">
            <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-background-200 bg-background-50">
              <h3 className="flex items-center gap-2 font-heading font-bold text-foreground-950 text-base">
                <i className="ri-timer-flash-fill text-primary-500 text-xl" />
                分秒速递 · 火情处置时间线
              </h3>
              <span className="inline-flex items-center gap-1.5 text-xs text-primary-600 font-medium">
                <i className="ri-live-line" /> 直播中 · 第 38 分钟
              </span>
            </div>
            <div className="p-4 md:p-5 overflow-x-auto">
              <div className="flex gap-4 min-w-max">
                {liveTimeline.map((t, idx) => (
                  <div key={idx} className="relative flex flex-col w-52 md:w-60 rounded-md bg-background-50 border border-background-200 p-3.5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-2 h-2 rounded-full ${kindColor[t.kind]}`} />
                      <span className="font-mono text-sm font-bold text-foreground-950">{t.time}</span>
                    </div>
                    <span className="inline-flex items-center text-[10px] text-primary-600 font-semibold tracking-wider mb-1.5">{t.kind} · {t.title}</span>
                    <p className="text-xs text-foreground-600 leading-relaxed">{t.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}